import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { BorderGradient } from 'processhub-components-lib';
import { FiX, FiCalendar } from 'react-icons/fi';

interface ApiKey {
  apiKey: string;
  description: string;
  permissions: string;
  expiryDate: string;
  createdDate: string;
  generatedOn: string;
}

interface ApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ApiKey) => void;
  initialData?: ApiKey | null;
}

const generateRandomKey = () =>
  Math.random().toString(36).substring(2, 10) + '-' + Date.now().toString(36);

const ApiKeyModal: React.FC<ApiKeyModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  useEffect(() => {
    if (initialData) {
      setApiKey(initialData.apiKey);
      setDescription(initialData.description);
      setPermissions(initialData.permissions);
      setExpiryDate(initialData.expiryDate);
    } else {
      setApiKey(generateRandomKey());
      setDescription('');
      setPermissions('');
      setExpiryDate('');
    }
  }, [initialData, isOpen]);

  const handleSave = () => {
    if (!description || !permissions || !expiryDate) {
      alert('Please fill out all required fields.');
      return;
    }

    const today = new Date();
    const createdDate = initialData?.createdDate || today.toLocaleDateString('en-GB');
    const generatedOn = today.toLocaleDateString();

    onSave({
      apiKey,
      description,
      permissions,
      expiryDate,
      createdDate,
      generatedOn,
    });
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] lg:w-[1024px] h-[70vh]">
          <BorderGradient className="rounded-lg flex flex-col h-full w-full">
            <div className="bg-[#1A1A1A] rounded-lg p-6 relative text-white overflow-y-auto flex flex-col h-full">
              <button
                onClick={onClose}
                className="absolute top-[28px] right-4 text-gray-400 hover:text-white"
                aria-label="Close"
              >
                <FiX size={24} />
              </button>
              <h2 className="text-lg font-semibold mb-8">
                {initialData ? 'Edit API Key' : 'Generate API Key'}
              </h2>
              <div className="flex flex-col gap-6 flex-1">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">API Key</label>
                  <input
                    className="w-full bg-[#232323] text-white rounded px-3 py-2 focus:outline-none"
                    value={apiKey}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Description</label>
                  <textarea
                    className="w-full bg-[#232323] text-white rounded px-3 py-2 focus:outline-none min-h-[60px]"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Permissions</label>
                  <div className="flex gap-6 bg-[#232323] rounded p-3">
                    {['both', 'read', 'write'].map(perm => (
                      <label key={perm} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="permissions"
                          checked={permissions === perm}
                          onChange={() => setPermissions(perm)}
                          className="accent-blue-500"
                        />
                        {perm === 'both'
                          ? 'Both'
                          : perm.charAt(0).toUpperCase() + perm.slice(1) + ' only'}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Expiry date</label>
                  <div className="relative">
                    <input
                      type="date"
                      className="w-full bg-[#232323] text-white rounded px-3 py-2 pr-10"
                      value={expiryDate}
                      onChange={e => setExpiryDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-4 justify-end mt-8">
                <button
                   className="flex items-center justify-center text-center gap-2 px-4 py-2 w-[140px] text-white rounded-lg hover:bg-[#3a3a3a33] transition-colors"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                 className="flex items-cente justify-center gap-2 px-4 py-2 w-[140px] bg-[#2A2A2A] text-white rounded-lg hover:bg-[#3a3a3a33] transition-colors"
                  onClick={handleSave}
                >
                  Save
                </button>
              </div>
            </div>
          </BorderGradient>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ApiKeyModal;
